<script setup>
import { computed, onMounted } from "vue";
import AnalyticsHeader from "./analytics/components/AnalyticsHeader.vue";
import AnalyticsKpiGrid from "./analytics/components/AnalyticsKpiGrid.vue";
import AnalyticsCharts from "./analytics/components/AnalyticsCharts.vue";
import AnalyticsEmptyState from "./analytics/components/AnalyticsEmptyState.vue";
import AnalyticsSkeleton from "./analytics/components/AnalyticsSkeleton.vue";
import { useAnalytics } from "./analytics/composables/useAnalytics";

const { interval, loading, error, overview, incidences, attemptsDistribution, isEmpty, load } =
  useAnalytics("this_month");

onMounted(() => {
  void load();
});

// ── Rendimiento ─────────────────────────────────────────────────────────────
// 4 KPIs de primer nivel — usan col-lg-3 (4 por fila en pantalla grande)
const performanceKpis = computed(() => [
  kpi({
    key: "stops",
    title: "Paradas totales",
    value: formatInteger(overview.value.total_stops),
    current: overview.value.total_stops,
    total: resolveTotalStopsBase(),
    isPercentageMetric: true,
    progressColor: "en_curso",
    metaLabel: "Incluye todos los estados",
  }),
  kpi({
    key: "success",
    title: "Tasa de éxito",
    value: formatPercent(overview.value.success_rate),
    current: overview.value.success_rate,
    total: 100,
    isPercentageMetric: true,
    progressColor: "finalizado",
    metaLabel: "Paradas completadas o parciales",
  }),
  kpi({
    key: "first_attempt",
    title: "Primer intento",
    value: formatPercent(overview.value.first_attempt_success_rate),
    current: overview.value.first_attempt_success_rate,
    total: 100,
    isPercentageMetric: true,
    progressColor: "finalizado",
    metaLabel: "Éxito en el primer intento de entrega",
  }),
  kpi({
    key: "incidence",
    title: "Tasa incidencias",
    value: formatPercent(overview.value.incidence_rate),
    current: overview.value.incidence_rate,
    total: 100,
    isPercentageMetric: true,
    progressColor: "error",
    metaLabel: "Paradas con incidencia",
  }),
]);

// ── Estado de paradas ────────────────────────────────────────────────────────
// 6 KPIs — usan col-lg-2 (6 por fila en pantalla grande)
const statusKpis = computed(() => [
  kpi({
    key: "assigned",
    title: "Asignadas",
    value: formatInteger(overview.value.assigned_stops),
    current: overview.value.assigned_stops,
    total: resolveTotalStopsBase(),
    progressColor: "en_curso",
    metaLabel: "Paradas en ruta actualmente",
  }),
  kpi({
    key: "unassigned",
    title: "No asignadas",
    value: formatInteger(overview.value.unassigned_stops),
    current: overview.value.unassigned_stops,
    total: resolveTotalStopsBase(),
    progressColor: "pendiente",
    metaLabel: "Pendientes de asignar a una ruta",
  }),
  kpi({
    key: "retry",
    title: "Reintentos",
    value: formatInteger(overview.value.retry_stops),
    current: overview.value.retry_stops,
    total: resolveTotalStopsBase(),
    progressColor: "pendiente",
    metaLabel: "Paradas que han necesitado reintento",
  }),
  kpi({
    key: "cancelled",
    title: "Excluidas",
    value: formatInteger(overview.value.cancelled_stops),
    current: overview.value.cancelled_stops,
    total: resolveTotalStopsBase(),
    progressColor: "cancelado",
    metaLabel: "Canceladas en el periodo",
  }),
  kpi({
    key: "avg",
    title: "Intentos medios",
    value: formatDecimal(overview.value.avg_attempts),
    current: overview.value.avg_attempts,
    total: resolveAvgAttemptsBase(),
    progressColor: "pendiente",
    metaLabel: "Media de intentos por parada",
  }),
  kpi({
    key: "plans",
    title: "Planes",
    value: formatInteger(overview.value.total_plans),
    current: overview.value.total_plans,
    total: resolvePlansBase(),
    isPercentageMetric: true,
    progressColor: "en_curso",
    metaLabel: "Planes del periodo",
  }),
]);

// ── Flota y servicio ─────────────────────────────────────────────────────────
// 4 KPIs — usan col-lg-3 (4 por fila en pantalla grande)
const fleetKpis = computed(() => [
  kpi({
    key: "routes",
    title: "Rutas",
    value: formatInteger(overview.value.total_routes),
    current: overview.value.total_routes,
    total: resolveTotalRoutesBase(),
    isPercentageMetric: true,
    progressColor: "en_curso",
    metaLabel: "Creadas en el periodo",
  }),
  kpi({
    key: "vehicles",
    title: "Vehículos activos",
    value: formatInteger(overview.value.active_vehicles),
    current: overview.value.active_vehicles,
    total: resolveActiveVehiclesBase(),
    progressColor: "en_curso",
    metaLabel: "Con al menos una ruta en el periodo",
  }),
  kpi({
    key: "deliveries",
    title: "Entregas",
    value: formatInteger(overview.value.total_deliveries),
    current: overview.value.total_deliveries,
    total: resolveTotalStopsBase(),
    progressColor: "finalizado",
    metaLabel: "% del total de paradas",
  }),
  kpi({
    key: "pickups",
    title: "Recogidas",
    value: formatInteger(overview.value.total_pickups),
    current: overview.value.total_pickups,
    total: resolveTotalStopsBase(),
    progressColor: "pendiente",
    metaLabel: "% del total de paradas",
  }),
]);

function kpi(item) {
  return {
    ...item,
    percentage: calculatePercentage(item.current, item.total),
  };
}

function formatInteger(value) {
  return new Intl.NumberFormat("es-ES").format(normalizeNumber(value));
}

function formatPercent(value) {
  return `${normalizeNumber(value).toFixed(1)}%`;
}

function formatDecimal(value) {
  return normalizeNumber(value).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function normalizeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function calculatePercentage(value, total) {
  const safeTotal = normalizeNumber(total);
  if (safeTotal <= 0) return 0;
  const percentage = (normalizeNumber(value) / safeTotal) * 100;
  return Math.max(0, Math.min(100, Math.round(percentage)));
}

function resolveTotalStopsBase() {
  return Math.max(normalizeNumber(overview.value.total_stops), 1);
}

function resolveTotalRoutesBase() {
  return Math.max(normalizeNumber(overview.value.total_routes), 1);
}

function resolveActiveVehiclesBase() {
  return Math.max(
    normalizeNumber(overview.value.active_vehicles),
    normalizeNumber(overview.value.total_routes),
    1
  );
}

function resolvePlansBase() {
  return Math.max(normalizeNumber(overview.value.total_plans), 1);
}

function resolveAvgAttemptsBase() {
  return Math.max(normalizeNumber(overview.value.avg_attempts), 3);
}
</script>

<template>
  <div class="container-fluid">
    <analytics-header v-model="interval" />

    <div v-if="loading" class="mt-3">
      <analytics-skeleton />
    </div>

    <template v-else>
      <div v-if="error" class="mt-3">
        <div class="card card-detail-layout">
          <div class="card-body text-center py-5">
            <h6 class="mb-2">No se pudo cargar Analytics</h6>
            <p class="text-sm text-secondary mb-3">{{ error }}</p>
            <button class="btn btn-primary mb-0" @click="load">Reintentar</button>
          </div>
        </div>
      </div>

      <template v-else>
        <div v-if="isEmpty" class="mt-0">
          <analytics-empty-state />
        </div>

        <template v-else>
          <div class="mt-0">
            <p class="section-label">Rendimiento</p>
            <analytics-kpi-grid :items="performanceKpis" col-class="col-12 col-sm-6 col-lg-3" />
          </div>

          <div class="mt-3">
            <p class="section-label">Estado de paradas</p>
            <analytics-kpi-grid :items="statusKpis" />
          </div>

          <div class="mt-3">
            <p class="section-label">Flota y servicio</p>
            <analytics-kpi-grid :items="fleetKpis" col-class="col-12 col-sm-6 col-lg-3" />
          </div>

          <div class="mt-3">
            <analytics-charts
              :overview="overview"
              :incidences="incidences"
              :attempts-distribution="attemptsDistribution"
            />
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<style scoped>
.section-label {
  align-items: center;
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  display: inline-flex;
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
  padding: 0.28rem 0.55rem;
  width: fit-content;
}
</style>
